import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../button';

interface PaginationProps {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
  showTotal?: (total: number, range: [number, number]) => React.ReactNode;
  showSizeChanger?: boolean;
  size?: 'small' | 'default';
}

const Pagination: React.FC<PaginationProps> = ({
  current,
  pageSize,
  total,
  onChange,
  showTotal,
  size = 'default',
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (current - 1) * pageSize + 1;
  const endItem = Math.min(current * pageSize, total);

  if (total === 0) return null;

  const handlePrevious = () => {
    if (current > 1) {
      onChange(current - 1, pageSize);
    }
  };

  const handleNext = () => {
    if (current < totalPages) {
      onChange(current + 1, pageSize);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== current) {
      onChange(page, pageSize);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current > 3) {
        pages.push('...');
      }

      // Show pages around current
      const start = Math.max(2, current - 1);
      const end = Math.min(totalPages - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const isSmall = size === 'small';
  const buttonSize = isSmall ? 'h-8 w-8' : 'h-9 w-9';
  const iconSize = isSmall ? 'h-4 w-4' : 'h-4 w-4';

  return (
    <div className="flex items-center gap-2">
      {showTotal && (
        <span className="text-sm text-muted-foreground mr-2">
          {showTotal(total, [startItem, endItem])}
        </span>
      )}

      <nav className="flex items-center gap-1" aria-label="Pagination">
        {/* Previous button */}
        <Button
          variant="outline"
          className={cn(buttonSize, 'p-0')}
          onClick={handlePrevious}
          disabled={current === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className={iconSize} />
        </Button>

        {/* Page numbers */}
        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className={cn(
                  buttonSize,
                  'flex items-center justify-center text-muted-foreground',
                )}
              >
                <MoreHorizontal className="h-4 w-4" />
              </span>
            );
          }

          const pageNum = page as number;
          const isActive = pageNum === current;

          return (
            <Button
              key={pageNum}
              variant={isActive ? 'default' : 'outline'}
              className={cn(buttonSize, 'p-0')}
              onClick={() => handlePageClick(pageNum)}
              aria-label={`Page ${pageNum}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNum}
            </Button>
          );
        })}

        {/* Next button */}
        <Button
          variant="outline"
          className={cn(buttonSize, 'p-0')}
          onClick={handleNext}
          disabled={current === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className={iconSize} />
        </Button>
      </nav>
    </div>
  );
};

export default Pagination;
